import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import { GET_BUILD_STATUS, GET_BUILD_LOGS } from '../graphql/queries';
import { STOP_WORKFLOW, SUSPEND_WORKFLOW, RESUME_WORKFLOW, RETRY_WORKFLOW, RESUBMIT_WORKFLOW } from '../graphql/mutations';
import { PipelineDAG } from '../components/PipelineDAG';
import OsIcon, { PackerBuildIcon } from '../components/OsIcon';
import { useLogStream } from '../hooks/useLogStream';

function phaseBadge(phase) {
  const map = { Succeeded: 'success', Running: 'info', Failed: 'error', Pending: 'pending', Error: 'error' };
  return <span className={`badge badge-${map[phase] || 'pending'}`}>{phase}</span>;
}

// Keyword coloring patterns for log output
const LOG_PATTERNS = [
  { regex: /\b(error|ERROR|Error|FATAL|fatal|Fatal|PANIC|panic)\b/g, className: 'log-error' },
  { regex: /\b(fail|FAIL|Fail|failed|FAILED|Failed|failure|FAILURE|Failure)\b/g, className: 'log-error' },
  { regex: /\b(warn|WARN|Warn|warning|WARNING|Warning)\b/g, className: 'log-warn' },
  { regex: /\b(success|SUCCESS|Success|succeeded|SUCCEEDED|Succeeded|passed|PASSED)\b/g, className: 'log-success' },
  { regex: /\b(info|INFO|Info)\b/g, className: 'log-info' },
];

function highlightLogLine(line, lineIndex) {
  // Find all matches across all patterns
  const matches = [];
  for (const { regex, className } of LOG_PATTERNS) {
    // Reset regex lastIndex since we reuse them
    regex.lastIndex = 0;
    let match;
    while ((match = regex.exec(line)) !== null) {
      matches.push({ start: match.index, end: match.index + match[0].length, text: match[0], className });
    }
  }

  if (matches.length === 0) return line;

  // Sort by position
  matches.sort((a, b) => a.start - b.start);

  // Build segments
  const segments = [];
  let cursor = 0;
  for (const m of matches) {
    if (m.start < cursor) continue; // skip overlapping
    if (m.start > cursor) {
      segments.push(line.slice(cursor, m.start));
    }
    segments.push(<span key={`${lineIndex}-${m.start}`} className={m.className}>{m.text}</span>);
    cursor = m.end;
  }
  if (cursor < line.length) {
    segments.push(line.slice(cursor));
  }

  return segments;
}

function HighlightedLogs({ text }) {
  if (!text) return 'No logs available for this step.';

  const lines = text.split('\n');
  return lines.map((line, i) => (
    <React.Fragment key={i}>
      {i > 0 && '\n'}
      <span className="log-line-num">{String(i + 1).padStart(4, ' ')}  </span>
      {highlightLogLine(line, i)}
    </React.Fragment>
  ));
}

export default function BuildStatus() {
  const { workflowName } = useParams();
  const [selectedNode, setSelectedNode] = useState(null);
  const logEndRef = useRef(null);
  const isComplete = useRef(false);

  const { data, loading, error, stopPolling } = useQuery(GET_BUILD_STATUS, {
    variables: { workflowName },
    pollInterval: 5000,
  });

  const [fetchLogs, { data: logsData, loading: logsLoading }] = useLazyQuery(GET_BUILD_LOGS, {
    fetchPolicy: 'network-only',
  });

  const [stopWorkflow, { loading: stopping }] = useMutation(STOP_WORKFLOW);
  const [suspendWorkflow, { loading: suspending }] = useMutation(SUSPEND_WORKFLOW);
  const [resumeWorkflow, { loading: resuming }] = useMutation(RESUME_WORKFLOW);
  const [retryWorkflow, { loading: retrying }] = useMutation(RETRY_WORKFLOW);
  const [resubmitWorkflow, { loading: resubmitting }] = useMutation(RESUBMIT_WORKFLOW);

  const status = data?.getBuildStatus;
  const buildComplete = status && ['Succeeded', 'Failed', 'Error'].includes(status.phase);
  const buildFailed = status && ['Failed', 'Error'].includes(status.phase);
  const isSuspended = status?.phase === 'Running' && status?.nodes?.some(n => n.phase === 'Suspended');
  const isRunning = status && !buildComplete;

  // Live log streaming
  const {
    text: streamedText,
    status: streamStatus,
    lineCount: streamLineCount,
    reconnect: streamReconnect,
  } = useLogStream(workflowName, selectedNode?.podName || selectedNode?.id, {
    enabled: isRunning && !!selectedNode,
  });

  const useStream = streamStatus === 'streaming' || streamStatus === 'complete';
  const useFallback = streamStatus === 'fallback';

  // Stop polling build status once complete
  useEffect(() => {
    if (buildComplete && !isComplete.current) {
      isComplete.current = true;
      stopPolling();
    }
  }, [buildComplete, stopPolling]);

  // Fallback: poll logs via GraphQL when streaming is unavailable
  useEffect(() => {
    if (!selectedNode || buildComplete) return;
    if (!useFallback) return;

    const stepName = selectedNode.podName || selectedNode.id;
    const interval = setInterval(() => {
      fetchLogs({ variables: { workflowName, stepName } });
    }, 5000);
    // Fetch immediately on fallback
    fetchLogs({ variables: { workflowName, stepName } });

    return () => clearInterval(interval);
  }, [selectedNode, buildComplete, useFallback, workflowName, fetchLogs]);

  // Determine which logs to display
  const polledLogs = logsData?.getBuildLogs || '';
  const logs = useStream ? streamedText : polledLogs;

  // Smart auto-scroll: only scroll if user is already near the bottom
  const userScrolledUp = useRef(false);
  useEffect(() => {
    const el = logEndRef.current;
    if (el && logs && !userScrolledUp.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [logs]);

  const handleLogScroll = useCallback(() => {
    const el = logEndRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
    userScrolledUp.current = !nearBottom;
  }, []);

  const handleNodeClick = useCallback((node) => {
    setSelectedNode(node);
    fetchLogs({ variables: { workflowName, stepName: node.podName || node.id } });
  }, [workflowName, fetchLogs]);

  if (loading && !data) return <div className="loading">Loading build status...</div>;
  if (error) return <div className="error">Failed to load status: {error.message}</div>;
  if (!status) return <div className="error">Build not found</div>;

  return (
    <div>
      <h2 className="page-title"><PackerBuildIcon size={24} /> Build: <OsIcon name={workflowName} size={22} fallback="" /> {workflowName}</h2>
      <div className="card">
        <div className="detail-grid">
          <div className="detail-item"><label>Phase</label><p>{phaseBadge(status.phase)}</p></div>
          <div className="detail-item"><label>Progress</label><p>{status.progress || '-'}</p></div>
          <div className="detail-item"><label>Started</label><p>{status.startedAt ? new Date(status.startedAt).toLocaleString() : '-'}</p></div>
          <div className="detail-item"><label>Finished</label><p>{status.finishedAt ? new Date(status.finishedAt).toLocaleString() : '-'}</p></div>
          {status.startedAt && status.finishedAt && (
            <div className="detail-item"><label>Duration</label><p>{(() => {
              const ms = new Date(status.finishedAt) - new Date(status.startedAt);
              const mins = Math.floor(ms / 60000);
              const secs = Math.round((ms % 60000) / 1000);
              return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
            })()}</p></div>
          )}
        </div>
        {!buildComplete && <p style={{ marginTop: 12, color: '#636e72', fontSize: 13 }}>Status auto-refreshing every 5 seconds...</p>}
        <div className="actions" style={{ marginTop: 12 }}>
          {isRunning && !isSuspended && (
            <>
              <button className="btn" style={{ background: '#fdcb6e', color: '#2d3436', fontSize: 12, padding: '6px 14px' }}
                disabled={suspending} onClick={() => suspendWorkflow({ variables: { workflowName } })}>
                {suspending ? 'Suspending...' : 'Suspend'}
              </button>
              <button className="btn btn-danger" style={{ fontSize: 12, padding: '6px 14px' }}
                disabled={stopping} onClick={() => stopWorkflow({ variables: { workflowName } })}>
                {stopping ? 'Stopping...' : 'Cancel Build'}
              </button>
            </>
          )}
          {isRunning && isSuspended && (
            <button className="btn btn-primary" style={{ fontSize: 12, padding: '6px 14px' }}
              disabled={resuming} onClick={() => resumeWorkflow({ variables: { workflowName } })}>
              {resuming ? 'Resuming...' : 'Resume'}
            </button>
          )}
          {buildFailed && (
            <>
              <button className="btn btn-primary" style={{ fontSize: 12, padding: '6px 14px' }}
                disabled={retrying} onClick={() => retryWorkflow({ variables: { workflowName } })}>
                {retrying ? 'Retrying...' : 'Retry Failed Steps'}
              </button>
              <button className="btn" style={{ background: '#6c5ce7', color: '#fff', fontSize: 12, padding: '6px 14px' }}
                disabled={resubmitting} onClick={() => resubmitWorkflow({ variables: { workflowName } })}>
                {resubmitting ? 'Resubmitting...' : 'Resubmit Build'}
              </button>
            </>
          )}
        </div>
      </div>

      {status.nodes?.length > 0 && (
        <div className="card">
          <h3 style={{ marginBottom: 12 }}>Pipeline DAG</h3>
          <PipelineDAG workflowNodes={status.nodes} onNodeClick={handleNodeClick} />
        </div>
      )}

      {selectedNode && (
        <div className="card">
          <h3>Logs: {selectedNode.name}</h3>
          <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            {phaseBadge(selectedNode.phase)}
            {selectedNode.message && <span style={{ color: '#636e72', fontSize: 13 }}>{selectedNode.message}</span>}
            {!buildComplete && useStream && (
              <span style={{ color: '#00b894', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#00b894', display: 'inline-block', animation: 'pulse 1.5s ease-in-out infinite' }} />
                Live streaming ({streamLineCount} lines)
              </span>
            )}
            {!buildComplete && useFallback && (
              <>
                <span style={{ color: '#fdcb6e', fontSize: 13 }}>Polling fallback (5s)</span>
                <button
                  className="btn"
                  style={{ padding: '2px 10px', fontSize: 12, background: '#dfe6e9' }}
                  onClick={streamReconnect}
                >
                  Retry stream
                </button>
              </>
            )}
            {!buildComplete && streamStatus === 'connecting' && (
              <span style={{ color: '#0984e3', fontSize: 13 }}>Connecting...</span>
            )}
            {buildComplete && (
              <button
                className="btn"
                style={{ padding: '2px 10px', fontSize: 12, background: '#dfe6e9' }}
                onClick={() => fetchLogs({ variables: { workflowName, stepName: selectedNode.podName || selectedNode.id } })}
                disabled={logsLoading}
              >
                {logsLoading ? 'Loading...' : 'Reload Logs'}
              </button>
            )}
            {buildComplete && logs && <span style={{ color: '#636e72', fontSize: 12 }}>Logs archived for history</span>}
          </div>
          {logsLoading && !logs ? (
            <div className="loading">Loading logs...</div>
          ) : (
            <div className="code-block" ref={logEndRef} style={{ maxHeight: 600, overflow: 'auto' }} onScroll={handleLogScroll}>
              <pre><code><HighlightedLogs text={logs} /></code></pre>
            </div>
          )}
        </div>
      )}

      {status.nodes?.length > 0 && !selectedNode && (
        <div className="card">
          <p style={{ color: '#636e72', fontSize: 13 }}>Click a node in the DAG above to view its logs.</p>
        </div>
      )}

      <div className="actions">
        <Link to="/history" className="btn" style={{ background: '#dfe6e9' }}>Back to Build History</Link>
      </div>
    </div>
  );
}
